import { config } from '../config/env';

/**
 * API utility functions
 * Uses the configured server URL from environment variables
 */

export const api = {
  /**
   * Get the base server URL
   */
  getServerUrl: () => config.serverUrl,

  /**
   * Get the API URL
   */
  getApiUrl: () => config.apiUrl,

  /**
   * Make a fetch request to the API
   */
  request: async (endpoint: string, options?: RequestInit) => {
    const url = endpoint.startsWith('http') 
      ? endpoint 
      : `${config.apiUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  },

  /**
   * GET request
   */
  get: async <T = any>(endpoint: string, options?: RequestInit): Promise<T> => {
    const response = await api.request(endpoint, { ...options, method: 'GET' });
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * POST request
   */
  post: async <T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> => {
    const response = await api.request(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * PUT request
   */
  put: async <T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> => {
    const response = await api.request(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * DELETE request
   */
  delete: async <T = any>(endpoint: string, options?: RequestInit): Promise<T> => {
    const response = await api.request(endpoint, { ...options, method: 'DELETE' });
    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }
    return response.json();
  },

  /**
   * Upload a file to the server
   * Uploads to Cloudflare Images and adds item to queue
   */
  uploadFile: async (file: File): Promise<{ imageId: string; item: any }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${config.apiUrl}/upload`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary for FormData
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Upload failed: ${response.statusText}`);
    }

    return response.json();
  },
};

